import React, { useState, useRef, useEffect } from "react";
import { FiX, FiMaximize, FiMinimize, FiInfo, FiDownload } from "react-icons/fi";
import { saveAs } from "file-saver";

const FullScreenView = ({
  image,
  baseImage,
  onClose
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [showFileInfo, setShowFileInfo] = useState(false);
  const containerRef = useRef(null);
  const imageWrapperRef = useRef(null);

  // Load images for preloading
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const afterImageRef = useRef(null);
  const beforeImageRef = useRef(null);

  // Reset slider position when images change
  useEffect(() => {
    setSliderPosition(50);
    setImagesLoaded(false);
    
    // Auto-enable comparison if we have both images
    if (image && baseImage) {
      setShowComparison(true);
    }
  }, [image, baseImage]);

  // Preload images to get dimensions
  useEffect(() => {
    if (image && baseImage && showComparison) {
      const afterImg = new Image();
      const beforeImg = new Image();
      let loadedCount = 0;

      const onLoad = () => {
        loadedCount++;
        if (loadedCount === 2) {
          setImagesLoaded(true);
        }
      };

      afterImg.onload = onLoad;
      beforeImg.onload = onLoad;

      afterImg.src = image;
      beforeImg.src = baseImage;
    }
  }, [image, baseImage, showComparison]);

  // Handle mouse events for the slider
  const handleMouseDown = (e) => {
    if (!showComparison) return;
    setIsDragging(true);
    updateSliderPosition(e);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updateSliderPosition(e);
      e.preventDefault();
    }
  };

  const handleTouchStart = (e) => {
    if (!showComparison) return;
    setIsDragging(true);
    updateSliderPositionTouch(e);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      updateSliderPositionTouch(e);
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Calculate slider position based on mouse/touch position
  const updateSliderPosition = (e) => {
    if (imageWrapperRef.current) {
      const rect = imageWrapperRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPosition(Math.min(Math.max(percentage, 0), 100));
    }
  };

  const updateSliderPositionTouch = (e) => {
    if (imageWrapperRef.current && e.touches && e.touches[0]) {
      const rect = imageWrapperRef.current.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPosition(Math.min(Math.max(percentage, 0), 100));
    }
  };

  // Toggle comparison mode
  const toggleComparison = () => {
    setShowComparison(!showComparison);
  };
  
  // Toggle full width view
  const toggleFullWidth = () => {
    setIsFullWidth(!isFullWidth);
  };

  // Toggle file info
  const toggleFileInfo = () => {
    setShowFileInfo(!showFileInfo);
  };

  // Download function
  const handleDownload = async () => {
    try {
      if (!image) {
        console.error('No image to download');
        return;
      }

      console.log('Starting download for image:', image);

      // Method 1: Try direct saveAs (works for same-origin images and data URLs)
      try {
        const fileName = `ambaram_image_${Date.now()}.jpg`;
        saveAs(image, fileName);
        console.log('Download initiated successfully');
        return;
      } catch (directError) {
        console.log('Direct download failed, trying fetch method:', directError);
      }

      // Method 2: Fetch the image and convert to blob (handles CORS)
      try {
        const response = await fetch(image, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const fileName = `ambaram_image_${Date.now()}.jpg`;
        saveAs(blob, fileName);
        console.log('Fetch download completed successfully');
        return;
      } catch (fetchError) {
        console.log('Fetch download failed, trying proxy method:', fetchError);
      }

      // Method 3: Fallback - create temporary link (basic method)
      try {
        const link = document.createElement('a');
        link.href = image;
        link.download = `ambaram_image_${Date.now()}.jpg`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('Link download method completed');
      } catch (linkError) {
        console.error('All download methods failed:', linkError);
        // Show user-friendly error message
        alert('Download failed. You can right-click on the image and select "Save image as..." to download manually.');
      }

    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again or right-click on the image and select "Save image as..." to download manually.');
    }
  };

  // Update image dimensions when images load
  const handleImageLoad = (e) => {
    if (afterImageRef.current) {
      setImageDimensions({
        width: afterImageRef.current.naturalWidth,
        height: afterImageRef.current.naturalHeight
      });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(135deg, #232526 0%, #1c1c1c 100%)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
      }}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top gradient overlay */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "120px",
        background: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
        pointerEvents: "none",
        zIndex: 10
      }} />

      {/* Bottom gradient overlay */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "120px",
        background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
        pointerEvents: "none",
        zIndex: 10
      }} />

      {/* Top navigation bar */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        padding: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        zIndex: 1200
      }}>
        <div style={{ 
          color: "white", 
          fontWeight: 600, 
          fontSize: "1.1rem",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          <div style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: "#4D7CFE",
            marginRight: "4px"
          }}></div>
          {showComparison ? "Image Comparison" : "Image Viewer"}
        </div>

        <div style={{
          display: "flex",
          gap: "16px",
          alignItems: "center"
        }}>
          <ActionButton
            icon={<FiDownload size={18} />}
            tooltip="Download Image"
            onClick={handleDownload}
          />
          <ActionButton
            icon={<FiInfo size={18} />}
            tooltip="Image Info"
            onClick={toggleFileInfo}
            active={showFileInfo}
          />
          <ActionButton
            icon={isFullWidth ? <FiMinimize size={18} /> : <FiMaximize size={18} />}
            tooltip={isFullWidth ? "Exit Fullscreen" : "Fullscreen"}
            onClick={toggleFullWidth}
          />
          <ActionButton
            icon={<FiX size={22} />}
            tooltip="Close"
            onClick={onClose}
          />
        </div>
      </div>

      {/* Main container */}
      <div
        ref={containerRef}
        style={{
          width: isFullWidth ? "100%" : "90%",
          height: isFullWidth ? "100%" : "85%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          transition: "width 0.3s ease, height 0.3s ease",
          borderRadius: isFullWidth ? 0 : "12px",
          overflow: "hidden",
        }}
      >
        {showComparison && baseImage ? (
          <div 
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              background: "linear-gradient(45deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)",
              boxShadow: isFullWidth ? "none" : "0 20px 60px rgba(0,0,0,0.4)"
            }}
          >
            {/* Image comparison container */}
            <div
              ref={imageWrapperRef}
              style={{
                position: "relative",
                maxWidth: "100%",
                maxHeight: "100%",
                cursor: "col-resize",
                userSelect: "none",
                overflow: "hidden",
              }}
              onMouseMove={handleMouseMove}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
            >
              {/* After (edited) image - always full width */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <img
                  ref={afterImageRef}
                  src={image}
                  alt="After"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "calc(100vh - 160px)",
                    objectFit: "contain"
                  }}
                  onLoad={handleImageLoad}
                />
              </div>
              
              {/* Before image container with clip */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  width: `${sliderPosition}%`,
                  overflow: "hidden",
                }}
              >
                <img
                  ref={beforeImageRef}
                  src={baseImage}
                  alt="Before"
                  style={{
                    display: "block",
                    maxWidth: afterImageRef.current ? afterImageRef.current.width : "none",
                    maxHeight: "calc(100vh - 160px)",
                    objectFit: "contain",
                    width: afterImageRef.current ? afterImageRef.current.width : "auto"
                  }}
                />
              </div>
              
              {/* Slider control line */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${sliderPosition}%`,
                  width: "2px",
                  backgroundColor: "white",
                  transform: "translateX(-50%)",
                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.7)",
                  pointerEvents: "none"
                }}
              ></div>
              
              {/* Slider handle */}
              <div
                style={{
                  position: "absolute",
                  left: `${sliderPosition}%`,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "34px",
                  height: "34px",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.7)",
                  cursor: "col-resize",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none"
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 8L5.5 5M2.5 8L5.5 11M2.5 8H13.5M13.5 8L10.5 5M13.5 8L10.5 11" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              {/* Labels */}
              <div
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "15px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  pointerEvents: "none",
                  opacity: sliderPosition > 15 ? 1 : 0,
                  transition: "opacity 0.2s ease-in-out"
                }}
              >
                BEFORE
              </div>
              
              <div
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  pointerEvents: "none",
                  opacity: sliderPosition < 85 ? 1 : 0,
                  transition: "opacity 0.2s ease-in-out"
                }}
              >
                AFTER
              </div>
            </div>
          </div>
        ) : (
          // Regular image view when not comparing
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "100%",
            maxHeight: "100%",
            background: "linear-gradient(45deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)",
            boxShadow: isFullWidth ? "none" : "0 20px 60px rgba(0,0,0,0.4)",
            width: "100%",
            height: "100%",
            borderRadius: isFullWidth ? 0 : "12px",
          }}>
            <img
              src={image}
              alt="Full Screen View"
              style={{
                maxWidth: "100%",
                maxHeight: isFullWidth ? "100vh" : "calc(100vh - 160px)",
                objectFit: "contain",
                display: "block"
              }}
            />
          </div>
        )}

        {/* File info overlay - shows when info button is clicked */}
        {showFileInfo && (
          <div style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            borderRadius: "8px",
            padding: "16px",
            color: "white",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 30,
            maxWidth: "300px",
            border: "1px solid rgba(255,255,255,0.1)"
          }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "600" }}>Image Info</h4>
            <div style={{ fontSize: "13px", color: "#bbb", marginBottom: "4px" }}>
              Dimensions: {imageDimensions.width} × {imageDimensions.height}px
            </div>
            <div style={{ fontSize: "13px", color: "#bbb", marginBottom: "4px" }}>
              Format: {image?.endsWith('.png') ? 'PNG' : image?.endsWith('.jpg') || image?.endsWith('.jpeg') ? 'JPEG' : 'Unknown'}
            </div>
            <div style={{ fontSize: "13px", color: "#bbb", marginTop: "12px", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "10px" }}>
              URL: <a href={image} target="_blank" rel="noopener noreferrer" style={{ color: "#4D7CFE", textDecoration: "none", wordBreak: "break-all" }}>{image?.substring(0, 40)}...</a>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer control bar */}
      <div style={{
        position: "absolute",
        bottom: "30px",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1200,
        borderRadius: "12px",
        backgroundColor: "rgba(30, 30, 30, 0.85)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        padding: "8px 16px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(255,255,255,0.06)"
      }}>
        {baseImage && (
          <button
            onClick={toggleComparison}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "10px 24px",
              background: showComparison 
                ? "linear-gradient(90deg, #4D7CFE 0%, #5A8CFF 100%)" 
                : "rgba(255, 255, 255, 0.08)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              boxShadow: showComparison ? "0 4px 12px rgba(77, 124, 254, 0.3)" : "none",
              transition: "all 0.2s ease"
            }}
          >
            {showComparison ? "Hide Comparison" : "Show Comparison"}
          </button>
        )}
      </div>
      
      {/* Instruction overlay that fades away */}
      {showComparison && baseImage && (
        <div
          style={{
            position: "absolute",
            bottom: "100px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            opacity: isDragging ? 0 : 0.8,
            transition: "opacity 0.3s ease",
            textAlign: "center",
            pointerEvents: "none",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid rgba(255,255,255,0.1)"
          }}
        >
          Drag left or right to adjust comparison
        </div>
      )}
    </div>
  );
};

// Reusable action button component
const ActionButton = ({ icon, tooltip, onClick, active }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          backgroundColor: active ? "rgba(77, 124, 254, 0.8)" : "rgba(255, 255, 255, 0.1)",
          color: "white",
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          cursor: "pointer",
          boxShadow: active ? "0 4px 12px rgba(77, 124, 254, 0.3)" : "0 2px 10px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease"
        }}
      >
        {icon}
      </button>
      {showTooltip && (
        <div style={{
          position: "absolute",
          bottom: "-32px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "white",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
        }}>
          {tooltip}
      </div>
      )}
    </div>
  );
};

export default FullScreenView;
